import React from 'react';
import { Plus } from 'lucide-react';
import CRMCard from './CRMCard';
import { StrictModeDroppable } from './StrictModeDroppable';
import { useCRM } from '../../context/CRMContext';
import QuickAddCard from './QuickAddCard';
import Price from '../../components/common/Price';
import { useAuth } from '../../context/AuthContext';

const COLUMN_STYLE = {
    new_inquiry:    { dot: '#6366F1', bar: '#6366F1', bg: '#EEF2FF', label: '#4F46E5', border: '#C7D2FE' },
    estimation:     { dot: '#F59E0B', bar: '#F59E0B', bg: '#FFFBEB', label: '#B45309', border: '#FDE68A' },
    site_visit:     { dot: '#F59E0B', bar: '#F59E0B', bg: '#FFFBEB', label: '#B45309', border: '#FDE68A' },
    proposal_sent:  { dot: '#06B6D4', bar: '#06B6D4', bg: '#ECFEFF', label: '#0891B2', border: '#A5F3FC' },
    negotiation:    { dot: '#8B5CF6', bar: '#8B5CF6', bg: '#F5F3FF', label: '#6D28D9', border: '#DDD6FE' },
    won:            { dot: '#10B981', bar: '#10B981', bg: '#ECFDF5', label: '#047857', border: '#A7F3D0' },
    booked:         { dot: '#10B981', bar: '#10B981', bg: '#ECFDF5', label: '#047857', border: '#A7F3D0' },
    sold:           { dot: '#10B981', bar: '#10B981', bg: '#ECFDF5', label: '#047857', border: '#A7F3D0' },
    lost:           { dot: '#EF4444', bar: '#EF4444', bg: '#FEF2F2', label: '#B91C1C', border: '#FECACA' },
    contacted:      { dot: '#3B82F6', bar: '#3B82F6', bg: '#EFF6FF', label: '#1D4ED8', border: '#BFDBFE' },
};
const DEFAULT_STYLE = { dot: '#9CA3AF', bar: '#9CA3AF', bg: '#F9FAFB', label: '#6B7280', border: '#E5E7EB' };

const CRMKanbanColumn = ({ column, columnId }) => {
    const { isQuickAddVisible, setIsQuickAddVisible, columns } = useCRM();
    const { checkAccess } = useAuth();

    const s = COLUMN_STYLE[columnId] || DEFAULT_STYLE;

    const totalBudget = column.items.reduce((sum, item) => sum + (Number(item.budget) || 0), 0);

    const totalAllLeads = columns
        ? Object.values(columns).reduce((acc, c) => acc + (c.items?.length || 0), 0)
        : 0;
    const progressPct = totalAllLeads > 0
        ? Math.round((column.items.length / totalAllLeads) * 100)
        : 0;

    return (
        <div
            className="flex flex-col shrink-0 h-full w-[272px] rounded-xl border border-gray-200 dark:border-[#30363d] bg-gray-50 dark:bg-[#161b22] overflow-hidden transition-colors"
        >
            {/* ── STICKY COLUMN HEADER ── */}
            <div
                className="sticky top-0 z-10 px-3.5 py-2.5 pb-2 border-b border-gray-200 dark:border-[#30363d] bg-gray-50 dark:bg-[#161b22] transition-colors"
            >
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-white">
                        {column.title}
                    </span>

                    {checkAccess('financial.view') && totalBudget > 0 && (
                        <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-transparent dark:border-slate-700/50"
                            style={{ background: s.dot + '20', color: s.dot }}
                        >
                            <Price amount={(totalBudget / 100000).toFixed(1)} />L
                        </span>
                    )}
                </div>

                {/* Progress bar — % of total leads in this column */}
                <div className="w-full h-1 bg-white dark:bg-[#30363d] rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${progressPct}%`, background: s.bar }}
                    />
                </div>
            </div>

            {/* ── SCROLLABLE CARDS AREA ── */}
            <StrictModeDroppable droppableId={columnId}>
                {(provided, snapshot) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto transition-colors bg-transparent rounded-b-xl"
                        style={{
                            minHeight: 120,
                            background: snapshot.isDraggingOver ? s.dot + '0D' : 'transparent',
                            boxShadow: snapshot.isDraggingOver ? `inset 0 0 0 1.5px ${s.dot}35` : 'none',
                            transition: 'background-color 180ms ease, box-shadow 180ms ease',
                        }}
                    >
                        {columnId === 'new_inquiry' && isQuickAddVisible && checkAccess('crm.create') && (
                            <QuickAddCard />
                        )}

                        {Array.isArray(column.items) && column.items.map((lead, index) => (
                            <CRMCard key={lead.id} lead={lead} index={index} />
                        ))}

                        {provided.placeholder}

                        {column.items.length === 0 && !isQuickAddVisible && (
                            <div className="flex items-center justify-center py-6 text-[11px] text-gray-400 dark:text-[#6e7681]">
                                No leads here
                            </div>
                        )}
                    </div>
                )}
            </StrictModeDroppable>

            {/* Add button */}
            {columnId === 'new_inquiry' && checkAccess('crm.create') && (
                <div className="px-2 pb-2 shrink-0 bg-gray-50 dark:bg-[#161b22]">
                    <button
                        onClick={() => setIsQuickAddVisible(true)}
                        className="flex items-center justify-center gap-1.5 w-full py-2 text-gray-500 dark:text-[#848d97] hover:text-gray-700 dark:hover:text-white bg-white dark:bg-[#21262d] border border-dashed border-gray-200 dark:border-[#30363d] hover:border-gray-400 dark:hover:border-[#848d97] rounded-lg transition text-[11px] font-bold uppercase tracking-wide"
                    >
                        <Plus size={12} strokeWidth={3} />Add Inquiry
                    </button>
                </div>
            )}
        </div>
    );
};

export default CRMKanbanColumn;